/**
 * Business Documents Management Hook
 * 
 * Manages required business documents for vendor verification:
 * - W-9 Form
 * - Business Registration
 * - Commercial General Liability (CGL) Insurance Certificate
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type BusinessDocumentType = 'w9' | 'business_registration' | 'cgl_insurance';

export interface BusinessDocuments {
  w9_document_url: string | null;
  w9_uploaded_at: string | null;
  business_registration_url: string | null;
  business_registration_uploaded_at: string | null;
  cgl_insurance_url: string | null;
  cgl_insurance_uploaded_at: string | null;
  cgl_insurance_expiry_date: string | null;
}

const DOCUMENT_LABELS: Record<BusinessDocumentType, string> = {
  w9: 'W-9 Form',
  business_registration: 'Business Registration',
  cgl_insurance: 'CGL Insurance Certificate',
};

// Column name mapping for each document type (matches vendor_profiles_enhanced schema)
const DOCUMENT_COLUMN_MAP: Record<BusinessDocumentType, { url: string; uploadedAt: string }> = {
  w9: { url: 'w9_document_url', uploadedAt: 'w9_uploaded_at' },
  business_registration: { url: 'business_registration_url', uploadedAt: 'business_registration_uploaded_at' },
  cgl_insurance: { url: 'cgl_insurance_url', uploadedAt: 'cgl_insurance_uploaded_at' },
};

const ALLOWED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const useBusinessDocuments = (
  vendorProfileId: string | null,
  isVerified: boolean = false
) => {
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const validateFile = useCallback((file: File): string | null => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload a PDF, JPG, or PNG file.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size exceeds 10MB limit.';
    }
    return null;
  }, []);

  const uploadDocument = useCallback(async (
    type: BusinessDocumentType,
    file: File,
    expiryDate?: Date
  ): Promise<boolean> => {
    if (!vendorProfileId) {
      toast({
        title: 'Error',
        description: 'Vendor profile not found',
        variant: 'destructive',
      });
      return false;
    }

    const validationError = validateFile(file);
    if (validationError) {
      toast({
        title: 'Validation Error',
        description: validationError,
        variant: 'destructive',
      });
      return false;
    }

    // For CGL insurance, expiry date is required
    if (type === 'cgl_insurance' && !expiryDate) {
      toast({
        title: 'Expiry Date Required',
        description: 'Please provide the insurance expiry date',
        variant: 'destructive',
      });
      return false;
    }

    setIsUploading(true);

    try {
      // Generate unique file path
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 8);
      const ext = file.name.split('.').pop()?.toLowerCase() || 'pdf';
      const storagePath = `verification-documents/${vendorProfileId}/${type}/${timestamp}-${randomId}.${ext}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(storagePath, file, {
          contentType: file.type,
          upsert: false,
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error('Failed to upload file');
      }

      // Build update object based on document type
      const updateData: Record<string, any> = {};
      const columns = DOCUMENT_COLUMN_MAP[type];
      updateData[columns.url] = storagePath;
      updateData[columns.uploadedAt] = new Date().toISOString();

      if (type === 'cgl_insurance' && expiryDate) {
        updateData.cgl_insurance_expiry_date = expiryDate.toISOString().split('T')[0];
      }

      // Update vendor profile
      const { error: updateError } = await supabase
        .from('vendor_profiles_enhanced')
        .update(updateData as any)
        .eq('id', vendorProfileId);

      if (updateError) {
        console.error('Update error:', updateError);
        throw new Error('Failed to save document reference');
      }

      // If vendor is already verified, notify admins of document update
      if (isVerified) {
        await notifyAdminsOfDocumentUpdate(vendorProfileId, type);
      }

      toast({
        title: 'Document Uploaded',
        description: `${DOCUMENT_LABELS[type]} uploaded successfully`,
      });

      return true;
    } catch (error: any) {
      console.error('Document upload error:', error);
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload document',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsUploading(false);
    }
  }, [vendorProfileId, isVerified, validateFile, toast]);

  const removeDocument = useCallback(async (
    type: BusinessDocumentType,
    currentUrl: string | null
  ): Promise<boolean> => {
    if (!vendorProfileId) {
      toast({
        title: 'Error',
        description: 'Vendor profile not found',
        variant: 'destructive',
      });
      return false;
    }

    setIsUploading(true);

    try {
      // Delete from storage if URL exists
      if (currentUrl) {
        const { error: deleteError } = await supabase.storage
          .from('documents')
          .remove([currentUrl]);

        if (deleteError) {
          console.warn('Storage delete warning:', deleteError);
          // Continue even if storage delete fails
        }
      }

      // Build update object
      const updateData: Record<string, any> = {};
      const columns = DOCUMENT_COLUMN_MAP[type];
      updateData[columns.url] = null;
      updateData[columns.uploadedAt] = null;

      if (type === 'cgl_insurance') {
        updateData.cgl_insurance_expiry_date = null;
      }

      // Update vendor profile
      const { error: updateError } = await supabase
        .from('vendor_profiles_enhanced')
        .update(updateData as any)
        .eq('id', vendorProfileId);

      if (updateError) {
        throw new Error('Failed to remove document reference');
      }

      toast({
        title: 'Document Removed',
        description: `${DOCUMENT_LABELS[type]} removed successfully`,
      });

      return true;
    } catch (error: any) {
      console.error('Document remove error:', error);
      toast({
        title: 'Remove Failed',
        description: error.message || 'Failed to remove document',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsUploading(false);
    }
  }, [vendorProfileId, toast]);

  const getDocumentUrl = useCallback(async (storagePath: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(storagePath, 3600); // 1 hour expiry

      if (error) {
        console.error('Signed URL error:', error);
        return null;
      }

      return data.signedUrl;
    } catch (error) {
      console.error('Get document URL error:', error);
      return null;
    }
  }, []);

  return {
    uploadDocument,
    removeDocument,
    getDocumentUrl,
    isUploading,
    validateFile,
  };
};

/**
 * Notify admins when a verified vendor updates a document
 */
async function notifyAdminsOfDocumentUpdate(
  vendorProfileId: string,
  documentType: BusinessDocumentType
) {
  try {
    // Get vendor business name
    const { data: vendor } = await supabase
      .from('vendor_profiles_enhanced')
      .select('business_name, organization:organizations!vendor_profiles_enhanced_organization_id_fkey(name)')
      .eq('id', vendorProfileId)
      .single();

    const businessName = vendor?.organization?.name || vendor?.business_name || 'A vendor';

    // Get all admin users
    const { data: admins } = await supabase
      .from('user_roles')
      .select('user_id')
      .eq('role', 'admin');

    if (admins && admins.length > 0) {
      // Create notifications for all admins
      const notifications = admins.map((admin) => ({
        user_id: admin.user_id,
        title: 'Vendor Document Updated',
        message: `${businessName} has updated their ${DOCUMENT_LABELS[documentType]} document`,
        notification_type: 'info',
        category: 'admin',
        action_url: '/admin/verification',
        is_read: false,
      }));

      await supabase.from('in_app_notifications').insert(notifications);
    }
  } catch (error) {
    console.error('Failed to notify admins:', error);
    // Don't throw - this is not critical
  }
}

/**
 * Helper to check if all required documents are uploaded
 * @param documents - The document URLs to check
 * @param requireBusinessRegistration - Whether business registration is required (default: true)
 * @param requireW9 - Whether W-9 document is required (default: true)
 */
export function checkAllDocumentsUploaded(
  documents: Partial<BusinessDocuments>,
  requireBusinessRegistration: boolean = true,
  requireW9: boolean = true
): boolean {
  const hasW9 = Boolean(documents.w9_document_url);
  const hasBusinessReg = Boolean(documents.business_registration_url);
  const hasCgl = Boolean(documents.cgl_insurance_url);
  
  const w9Check = requireW9 ? hasW9 : true;
  const businessRegCheck = requireBusinessRegistration ? hasBusinessReg : true;
  
  return w9Check && businessRegCheck && hasCgl;
}

/**
 * Get count of uploaded documents
 * @param documents - The document URLs to check
 * @param requireBusinessRegistration - Whether business registration is required (default: true)
 * @param requireW9 - Whether W-9 document is required (default: true)
 */
export function getUploadedDocumentCount(
  documents: Partial<BusinessDocuments>,
  requireBusinessRegistration: boolean = true,
  requireW9: boolean = true
): number {
  let count = 0;
  if (requireW9 && documents.w9_document_url) count++;
  if (requireBusinessRegistration && documents.business_registration_url) count++;
  if (documents.cgl_insurance_url) count++;
  return count;
}

/**
 * Get total required document count
 * @param requireBusinessRegistration - Whether business registration is required (default: true)
 * @param requireW9 - Whether W-9 document is required (default: true)
 */
export function getTotalRequiredDocumentCount(
  requireBusinessRegistration: boolean = true,
  requireW9: boolean = true
): number {
  let count = 1; // CGL Insurance is always required
  if (requireBusinessRegistration) count++;
  if (requireW9) count++;
  return count;
}
